// Debug overlay, toggled with G (spec section 3). Built early because it pays for
// itself across every later phase. Phase 1 shows position, Z, velocity, states, stamina,
// and camera angles. Later phases extend this with collision volumes, tool-cupboard radii,
// aim rays, resolved Z-bands, and hitboxes.

export interface DebugStats {
  fps: number;
  pos: { x: number; y: number; z: number };
  speed: number;
  grounded: boolean;
  crouching: boolean;
  sprinting: boolean;
  stamina: number;
  aiming: boolean; // ALT free-look held: camera aims, travel path locked
  moveYawDeg: number; // the yaw frame movement is interpreted in
  draws: number; // renderer draw calls (perf guardrail: target < 100)
  tris: number; // rendered triangles
  fly: boolean; // dev fly mode
  god: boolean; // dev god mode (no death) -- toggled by the button below
  cam: { azimuthDeg: number; polarDeg: number; zoom: number };
  camDist: number; // metres from the camera to the player (the real zoom-out distance)
}

export class DebugOverlay {
  visible = false;
  perfVisible = false; // the frame-cost diagnostics panel (dev only, toggled with P)
  onToggleFly: (() => void) | null = null;
  onToggleGod: (() => void) | null = null;
  private readonly el: HTMLDivElement;
  private readonly perfEl: HTMLDivElement;
  private readonly flyBtn: HTMLButtonElement;
  private readonly godBtn: HTMLButtonElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.style.cssText = [
      'position:fixed', 'top:12px', 'left:12px', 'z-index:10',
      'padding:10px 12px', 'border-radius:6px',
      'background:rgba(8,11,15,0.82)', 'border:1px solid rgba(120,140,160,0.25)',
      'color:#9fe0a0', 'font:12px/1.55 ui-monospace,Menlo,Consolas,monospace',
      'white-space:pre', 'pointer-events:none', 'display:none',
    ].join(';');
    document.body.appendChild(this.el);

    // Frame-cost diagnostics panel (top-right, so it never overlaps the main overlay top-left).
    this.perfEl = document.createElement('div');
    this.perfEl.style.cssText = [
      'position:fixed', 'top:12px', 'right:12px', 'z-index:10',
      'padding:10px 12px', 'border-radius:6px',
      'background:rgba(8,11,15,0.82)', 'border:1px solid rgba(120,140,160,0.25)',
      'color:#7fd0ff', 'font:12px/1.55 ui-monospace,Menlo,Consolas,monospace',
      'white-space:pre', 'pointer-events:none', 'display:none',
    ].join(';');
    document.body.appendChild(this.perfEl);

    this.flyBtn = document.createElement('button');
    this.flyBtn.textContent = 'Fly: OFF';
    this.flyBtn.style.cssText = [
      'position:fixed', 'top:232px', 'left:12px', 'z-index:11', 'display:none',
      'padding:5px 10px', 'background:#1b2430', 'color:#cfd8e3',
      'border:1px solid rgba(120,140,160,0.35)', 'border-radius:6px',
      'font:12px ui-monospace,Menlo,Consolas,monospace', 'cursor:pointer',
    ].join(';');
    this.flyBtn.addEventListener('click', () => this.onToggleFly?.());
    document.body.appendChild(this.flyBtn);

    // God-mode button (no-death), sits just below the Fly button. Replaces the old O keyboard shortcut.
    this.godBtn = document.createElement('button');
    this.godBtn.textContent = 'God: OFF';
    this.godBtn.style.cssText = [
      'position:fixed', 'top:264px', 'left:12px', 'z-index:11', 'display:none',
      'padding:5px 10px', 'background:#1b2430', 'color:#cfd8e3',
      'border:1px solid rgba(120,140,160,0.35)', 'border-radius:6px',
      'font:12px ui-monospace,Menlo,Consolas,monospace', 'cursor:pointer',
    ].join(';');
    this.godBtn.addEventListener('click', () => this.onToggleGod?.());
    document.body.appendChild(this.godBtn);
  }

  toggle(): void {
    this.visible = !this.visible;
    this.el.style.display = this.visible ? 'block' : 'none';
    this.flyBtn.style.display = this.visible ? 'block' : 'none';
    this.godBtn.style.display = this.visible ? 'block' : 'none';
  }

  // Frame-cost diagnostics panel (dev only). Returns the new state so the caller can gate the probe's work.
  togglePerf(): boolean {
    this.perfVisible = !this.perfVisible;
    this.perfEl.style.display = this.perfVisible ? 'block' : 'none';
    return this.perfVisible;
  }

  updatePerf(lines: string[]): void {
    if (!this.perfVisible) return;
    this.perfEl.textContent = lines.join('\n');
  }

  update(s: DebugStats): void {
    if (!this.visible) return;
    this.flyBtn.textContent = s.fly ? 'Fly: ON' : 'Fly: OFF';
    this.flyBtn.style.color = s.fly ? '#9fe0a0' : '#cfd8e3';
    this.godBtn.textContent = s.god ? 'God: ON' : 'God: OFF';
    this.godBtn.style.color = s.god ? '#7CFC00' : '#cfd8e3';
    const f1 = (n: number) => n.toFixed(1);
    const f2 = (n: number) => n.toFixed(2);
    const flag = (on: boolean) => (on ? 'on ' : 'off');
    this.el.textContent = [
      'OXIDE debug (`)',
      `fps      ${Math.round(s.fps)}`,
      `pos      X ${f1(s.pos.x)}  Y ${f1(s.pos.y)}  Z ${f1(s.pos.z)}`,
      `center   ${f1(Math.hypot(s.pos.x, s.pos.z))} m from map middle`,
      `speed    ${f2(s.speed)} u/s`,
      `state    grounded ${flag(s.grounded)}  crouch ${flag(s.crouching)}  sprint ${flag(s.sprinting)}`,
      `stamina  ${f1(s.stamina)}`,
      `move     camera-relative  frame ${f1(s.moveYawDeg)} deg  aim-lock ${flag(s.aiming)}`,
      `camera   az ${f1(s.cam.azimuthDeg)} deg  pitch ${f1(s.cam.polarDeg)} deg`,
      `zoom     ${f2(s.cam.zoom)}x  (${f1(s.camDist)} m from player)`,
      `render   ${s.draws} calls  ${Math.round(s.tris / 1000)}k tris`,
    ].join('\n');
  }
}
